import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Target, UserPlus, CalendarClock, MapPin, FileText, Trophy, Users, AlertCircle, Wrench, HardHat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CFG = [
  { key: "total_leads", label: "Total Leads", hindi: "कुल लीड्स", icon: Target, accent: "amber" },
  { key: "new_leads", label: "New Leads", hindi: "नई लीड्स", icon: UserPlus, accent: "blue" },
  { key: "todays_followups", label: "Today's Follow-ups", hindi: "आज के फॉलोअप", icon: CalendarClock, accent: "orange" },
  { key: "site_visits", label: "Site Visits", hindi: "साइट विजिट", icon: MapPin, accent: "purple" },
  { key: "quotations", label: "Quotations", hindi: "कोटेशन", icon: FileText, accent: "sky" },
  { key: "won_leads", label: "Won Leads", hindi: "सफल लीड्स", icon: Trophy, accent: "emerald" },
  { key: "total_customers", label: "Total Customers", hindi: "कुल ग्राहक", icon: Users, accent: "indigo" },
  { key: "open_complaints", label: "Open Complaints", hindi: "ओपन शिकायत", icon: AlertCircle, accent: "rose" },
  { key: "pending_services", label: "Pending Services", hindi: "पेंडिंग सर्विस", icon: Wrench, accent: "amber" },
  { key: "active_projects", label: "Active Projects", hindi: "एक्टिव प्रोजेक्ट", icon: HardHat, accent: "emerald" },
];

const accentMap = {
  amber: "from-amber-500/10 border-l-amber-500 text-amber-600",
  blue: "from-blue-500/10 border-l-blue-500 text-blue-600",
  orange: "from-orange-500/10 border-l-orange-500 text-orange-600",
  purple: "from-purple-500/10 border-l-purple-500 text-purple-600",
  sky: "from-sky-500/10 border-l-sky-500 text-sky-600",
  emerald: "from-emerald-500/10 border-l-emerald-500 text-emerald-600",
  indigo: "from-indigo-500/10 border-l-indigo-500 text-indigo-600",
  rose: "from-rose-500/10 border-l-rose-500 text-rose-600",
};

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/dashboard/stats").then(({data})=>setStats(data)).finally(()=>setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={{fontFamily:'Outfit'}}>
          Welcome back, {user?.name?.split(" ")[0]} <span className="text-amber-500">☀</span>
        </h1>
        <p className="text-slate-500 mt-1">Here's your solar business at a glance today.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {CFG.map((c) => {
          const Icon = c.icon;
          const cls = accentMap[c.accent];
          return (
            <Card key={c.key} className={`kpi-card bg-gradient-to-br ${cls} border-l-4 border-y border-r border-slate-200`}
              data-testid={`kpi-card-${c.key.replace(/_/g,'-')}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="w-5 h-5" />
                  <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">{c.hindi}</span>
                </div>
                <div className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>
                  {loading ? "—" : (stats[c.key] ?? 0)}
                </div>
                <div className="text-xs font-medium text-slate-600 mt-1">{c.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <img src="https://images.unsplash.com/photo-1660330589257-813305a4a383?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMHJvb2Z0b3AlMjB0ZWNobmljaWFuJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc4OTIxMDc3MXww&ixlib=rb-4.1.0&q=85"
                 alt="" className="w-full sm:w-56 h-32 object-cover rounded-lg" />
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{fontFamily:'Outfit'}}>Pipeline your solar leads faster</h3>
              <p className="text-sm text-slate-600 mt-1">
                Add a lead, schedule a site visit, generate a branded quotation, and convert to customer — all from one place. Track every kW you deliver.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
